import type { CSSProperties } from "react";

/**
 * The one place character portraits are rendered. Shows the uploaded portrait when
 * present, otherwise a deterministic initials avatar — so characters without an
 * image always render cleanly. Use the size tokens rather than ad-hoc image CSS.
 */
export type AvatarSize = "xs" | "sm" | "md" | "lg";
const PX: Record<AvatarSize, number> = { xs: 22, sm: 32, md: 48, lg: 104 };

interface Props {
  name: string;
  imageUrl?: string | null;
  size?: AvatarSize;
  /** Accent halo — useful in relationship visualizations. */
  ring?: boolean;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0]![0]! + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Stable hue from the name so each character keeps a consistent fallback colour. */
function hueOf(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

export function CharacterAvatar({ name, imageUrl, size = "md", ring, className, style, title }: Props) {
  const px = PX[size];
  const cls = `avatar avatar-${size}${ring ? " avatar-ring" : ""}${className ? ` ${className}` : ""}`;
  const dims: CSSProperties = { width: px, height: px, ...style };
  const label = title ?? name;
  if (imageUrl) {
    return (
      <span className={cls} style={dims} title={label}>
        <img src={imageUrl} alt={name} draggable={false} />
      </span>
    );
  }
  const h = hueOf(name || "?");
  return (
    <span
      className={`${cls} avatar-initials`}
      style={{ ...dims, fontSize: Math.round(px * 0.4), background: `hsl(${h} 42% 32%)`, color: `hsl(${h} 75% 86%)` }}
      title={label}
    >
      {initials(name || "?")}
    </span>
  );
}
